import { getStaticsBaseUrl } from '@oysterjs/core/config';
import type { CaraWorkspaceFieldsFragment } from '@oysterjs/ais/api/graphql';

type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/**
 * Resolve a human-readable title for any workspace artifact, regardless of subtype.
 * Falls back through every known title-bearing subobject and ultimately to "Untitled".
 */
export const getArtifactTitle = (artifact: Artifact): string =>
  artifact.document?.title ||
  artifact.pdf?.title ||
  artifact.templateForm?.title ||
  artifact.applicationFile?.title ||
  artifact.customForm?.title ||
  'Untitled';

export type ArtifactStaticsKind = 'html' | 'pdf' | 'custom-form' | 'template' | 'application-file';

/**
 * Build a versioned statics URL for an artifact. The path segment after `/agency/`
 * mirrors the artifact subtype.
 */
export const buildArtifactStaticsUrl = (
  kind: ArtifactStaticsKind,
  workspaceId: string,
  artifactId: string,
  version: number,
  extraParams: Record<string, string> = {}
): string => {
  const params = new URLSearchParams({ version: String(version), ...extraParams });
  return `${getStaticsBaseUrl()}/agency/${kind}/${workspaceId}/${artifactId}?${params.toString()}`;
};

/**
 * Statics URL for an agency file. Mirrors AgencyDocumentStaticsURL in
 * pkg/service/api/files/service.go, which derives it from the id alone — so a file
 * reached through search (SearchFileResult exposes no url) is still previewable.
 */
export const buildAgencyDocumentStaticsUrl = (fileId: string): string =>
  `${getStaticsBaseUrl()}/agency/doc/${fileId}`;

/** Built-in style options shown in the legacy DocumentDraftArtifact preview dialog. */
export const BUILTIN_STYLES: ReadonlyArray<{ styleId: string; label: string }> = [
  { styleId: 'builtin:classic-professional', label: 'Classic Professional' },
  { styleId: 'builtin:modern-minimalist', label: 'Modern Minimalist' },
  { styleId: 'builtin:warm-human', label: 'Warm Human' },
  { styleId: 'builtin:elegant-premium', label: 'Elegant Premium' },
  { styleId: 'builtin:technical-structured', label: 'Technical and Structured' }
];

/**
 * The newest version of an artifact, or undefined when the workspace carries
 * none by that id.
 *
 * For email, send still requires the newest version
 * (`requireConfirmedEmailArtifact`). Conversation cards and deep links open
 * the version they name; callers that need the live draft (send, mailbox
 * place) use this helper.
 */
export const newestArtifactVersion = (
  artifacts: Artifact[],
  artifactId: string
): Artifact | undefined => {
  let newest: Artifact | undefined;
  for (const candidate of artifacts) {
    if (candidate.id !== artifactId) continue;
    if (!newest || candidate.version > newest.version) newest = candidate;
  }
  return newest;
};
/**
 * How an artifact's version reads: "2" for what somebody asked for, "2.1" for Cara having gone
 * back over that same version without being asked again.
 *
 * A version nothing has revised is just its number. Most are, and a trailing ".0" on all of
 * them would be noise hiding the few that carry a revision.
 */
export const artifactVersionNumber = (artifact: {
  version: number;
  revision?: number | null;
}): string =>
  artifact.revision ? `${artifact.version}.${artifact.revision}` : `${artifact.version}`;
